import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiFetch } from "../../data/apiFetch";
import { DEGREE_KEYS, TITLE_KEYS } from "../../lib/academic";

const MAX_FILES = 3;
const MAX_BYTES = 8 * 1024 * 1024;
const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";
const ACCEPT_MIME = new Set(ACCEPT.split(","));

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#7269e3]";

function fmtSize(b) {
  if (b >= 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(b / 1024))} KB`;
}

function StatusChip({ status, pending, t }) {
  if (pending) {
    return (
      <span className="inline-flex rounded-full bg-amber-500/15 px-3 py-1 text-sm text-amber-200 ring-1 ring-amber-400/20">
        🕐 {t("credentials.status.SUBMITTED")}
      </span>
    );
  }
  const map = {
    APPROVED: "bg-emerald-500/15 text-emerald-300 ring-emerald-400/20",
    REJECTED: "bg-red-500/15 text-red-200 ring-red-400/20",
    NOT_SUBMITTED: "bg-white/10 text-white/70 ring-white/10",
  };
  const icon = status === "APPROVED" ? "✅ " : status === "REJECTED" ? "❌ " : "";
  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-sm ring-1 ${map[status] || map.NOT_SUBMITTED}`}>
      {icon}
      {t(`credentials.status.${status in map ? status : "NOT_SUBMITTED"}`)}
    </span>
  );
}

/**
 * Specialist-facing diploma / academic-title verification block.
 * Props: degree / academicTitle — the values currently chosen in the profile
 * form, used to pre-fill the submission.
 */
export default function CredentialVerification({ degree = "", academicTitle = "" }) {
  const { t } = useTranslation();
  const fileRef = useRef(null);

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [fDegree, setFDegree] = useState(degree || "");
  const [fTitle, setFTitle] = useState(academicTitle || "");
  const [institution, setInstitution] = useState("");
  const [files, setFiles] = useState([]);
  const [consent, setConsent] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch("/api/credentials/me");
      setInfo(data);
    } catch {
      setInfo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Keep the pre-fill in sync with the profile form until the user opens the form.
  useEffect(() => {
    if (!formOpen) {
      setFDegree(degree || "");
      setFTitle(academicTitle || "");
    }
  }, [degree, academicTitle, formOpen]);

  const errText = (code) => {
    const key = `credentials.errors.${code}`;
    const s = t(key);
    return s === key ? t("credentials.errors.generic") : s;
  };

  const addFiles = (list) => {
    setError("");
    const next = [...files];
    for (const f of Array.from(list || [])) {
      if (!ACCEPT_MIME.has(f.type)) { setError(errText("UNSUPPORTED_FILE_TYPE")); continue; }
      if (f.size > MAX_BYTES) { setError(errText("FILE_TOO_LARGE")); continue; }
      if (next.length >= MAX_FILES) { setError(errText("TOO_MANY_FILES")); break; }
      next.push(f);
    }
    setFiles(next);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    setError("");
    setNotice("");
    if (!fDegree && !fTitle) return setError(errText("DEGREE_OR_TITLE_REQUIRED"));
    if (!files.length) return setError(errText("FILES_REQUIRED"));
    if (!consent) return setError(errText("CONSENT_REQUIRED"));

    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("degree", fDegree);
    fd.append("academicTitle", fTitle);
    fd.append("institution", institution);
    fd.append("consent", "true");

    setBusy(true);
    try {
      await apiFetch("/api/credentials", { method: "POST", body: fd });
      setFiles([]);
      setConsent(false);
      setFormOpen(false);
      setNotice(t("credentials.sentNotice"));
      await load();
    } catch (e) {
      setError(errText(e?.message));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    if (!window.confirm(t("credentials.withdrawConfirm"))) return;
    setBusy(true);
    setError("");
    try {
      await apiFetch("/api/credentials/pending", { method: "DELETE" });
      setNotice(t("credentials.withdrawnNotice"));
      await load();
    } catch (e) {
      setError(errText(e?.message));
    } finally {
      setBusy(false);
    }
  };

  const status = info?.credentialStatus || "NOT_SUBMITTED";
  const pending = !!info?.pending;
  const latest = info?.latest;
  const showForm = !pending && (formOpen || status === "NOT_SUBMITTED" || status === "REJECTED");
  const degreeChanged = info?.verifiedDegree && degree && degree !== info.verifiedDegree;
  const titleChanged = info?.verifiedTitle && academicTitle && academicTitle !== info.verifiedTitle;

  return (
    <section className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-unbounded text-xl font-semibold">{t("credentials.title")}</h2>
          <p className="mt-2 max-w-xl text-sm text-white/65">{t("credentials.subtitle")}</p>
        </div>
        {!loading && <StatusChip status={status} pending={pending} t={t} />}
      </div>

      {loading ? (
        <div className="mt-4 text-sm text-white/60">{t("common.loading", "…")}</div>
      ) : (
        <div className="mt-4 space-y-4">
          {notice && (
            <div className="rounded-xl bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200 ring-1 ring-emerald-400/20">{notice}</div>
          )}

          {/* Confirmed values */}
          {(info?.verifiedDegree || info?.verifiedTitle) && (
            <div className="rounded-xl bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100 ring-1 ring-emerald-400/20">
              <div className="font-medium">{t("credentials.confirmedLabel")}</div>
              <div className="mt-1 flex flex-wrap gap-2">
                {info.verifiedDegree && <span>✓ {t(`academic.degree.${info.verifiedDegree}`)}</span>}
                {info.verifiedTitle && <span>✓ {t(`academic.title.${info.verifiedTitle}`)}</span>}
              </div>
              {(degreeChanged || titleChanged) && (
                <div className="mt-2 text-amber-200">{t("credentials.changedWarning")}</div>
              )}
            </div>
          )}

          {/* Badge requires identity verification too */}
          {!info?.identityVerified && (
            <div className="rounded-xl bg-white/5 px-4 py-3 text-sm text-white/70 ring-1 ring-white/10">
              ℹ️ {t("credentials.identityHint")}
            </div>
          )}

          {/* Pending submission */}
          {pending && latest && (
            <div className="rounded-xl bg-amber-500/10 px-4 py-3 text-sm text-amber-100 ring-1 ring-amber-400/20">
              <div>
                {t("credentials.pendingInfo", {
                  date: new Date(latest.submittedAt).toLocaleDateString(),
                  count: latest.files?.length || 0,
                })}
              </div>
              <button
                type="button"
                onClick={withdraw}
                disabled={busy}
                className="mt-3 rounded-lg border border-white/15 px-3 py-1.5 text-xs text-white/80 hover:bg-white/10 disabled:opacity-50"
              >
                {t("credentials.withdraw")}
              </button>
            </div>
          )}

          {/* Last rejection */}
          {!pending && latest?.status === "REJECTED" && latest.rejectionReason && (
            <div className="rounded-xl bg-red-500/10 px-4 py-3 text-sm text-red-100 ring-1 ring-red-400/20">
              <span className="font-medium">{t("credentials.rejectedReason")}:</span> {latest.rejectionReason}
            </div>
          )}

          {!pending && status === "APPROVED" && !formOpen && (
            <button
              type="button"
              onClick={() => setFormOpen(true)}
              className="rounded-xl border border-white/15 px-4 py-2 text-sm text-white/85 hover:bg-white/10"
            >
              {t("credentials.submitNew")}
            </button>
          )}

          {showForm && (
            <div className="space-y-4">
              <div className="rounded-xl bg-white/5 px-4 py-3 text-sm text-white/70 ring-1 ring-white/10">
                <div className="font-medium text-white/85">{t("credentials.whichDocs")}</div>
                <div className="mt-1">{t("credentials.whichDocsList")}</div>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm text-white/80">{t("academic.degreeLabel")}</label>
                  <select value={fDegree} onChange={(e) => setFDegree(e.target.value)} className={inputCls}>
                    <option value="">{t("academic.notSet")}</option>
                    {DEGREE_KEYS.map((k) => (
                      <option key={k} value={k}>{t(`academic.degree.${k}`)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm text-white/80">{t("academic.titleLabel")}</label>
                  <select value={fTitle} onChange={(e) => setFTitle(e.target.value)} className={inputCls}>
                    <option value="">{t("academic.notSet")}</option>
                    {TITLE_KEYS.map((k) => (
                      <option key={k} value={k}>{t(`academic.title.${k}`)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm text-white/80">{t("credentials.institutionLabel")}</label>
                  <input
                    value={institution}
                    onChange={(e) => setInstitution(e.target.value)}
                    maxLength={200}
                    placeholder={t("credentials.institutionPh")}
                    className={inputCls}
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm text-white/80">{t("credentials.filesLabel")}</label>
                <input
                  ref={fileRef}
                  type="file"
                  accept={ACCEPT}
                  multiple
                  onChange={(e) => addFiles(e.target.files)}
                  className="block w-full text-sm text-white/75 file:mr-3 file:rounded-lg file:border-0 file:bg-[#7269e3] file:px-3 file:py-2 file:text-sm file:text-white hover:file:bg-[#5f56d6]"
                />
                <div className="mt-1 text-xs text-white/45">{t("credentials.filesHint")}</div>
                {files.length > 0 && (
                  <ul className="mt-2 space-y-1 text-sm">
                    {files.map((f, i) => (
                      <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-3 rounded-lg bg-white/5 px-3 py-1.5">
                        <span className="truncate">📄 {f.name} <span className="text-white/45">· {fmtSize(f.size)}</span></span>
                        <button
                          type="button"
                          onClick={() => setFiles((arr) => arr.filter((_, j) => j !== i))}
                          className="text-white/50 hover:text-white"
                          aria-label={t("credentials.removeFile")}
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="rounded-xl bg-[#7269e3]/10 px-4 py-3 text-xs text-white/70 ring-1 ring-[#7269e3]/25">
                🔒 {t("credentials.privacyNote")}
              </div>

              <label className="flex items-start gap-2 text-sm text-white/80">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-[#7269e3]"
                />
                <span>
                  {t("credentials.consent")}{" "}
                  <Link to="/privacy" target="_blank" className="text-[#b9b3ff] underline">
                    {t("credentials.privacyLink")}
                  </Link>
                </span>
              </label>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={submit}
                  disabled={busy}
                  className="rounded-xl bg-[#7269e3] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#5f56d6] disabled:opacity-50"
                >
                  {busy ? t("credentials.sending") : t("credentials.submit")}
                </button>
                {status === "APPROVED" && (
                  <button
                    type="button"
                    onClick={() => { setFormOpen(false); setFiles([]); setError(""); }}
                    className="rounded-xl border border-white/15 px-4 py-2.5 text-sm text-white/80 hover:bg-white/10"
                  >
                    {t("common.cancel", "Cancel")}
                  </button>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-xl bg-red-500/10 px-4 py-3 text-sm text-red-200 ring-1 ring-red-400/20">{error}</div>
          )}
        </div>
      )}
    </section>
  );
}
